/**
 * wsSizeBudgetConfig — single source of truth for per-prefix Y.Doc size
 * ceilings on the client.
 *
 * `wsSizeBudget.ts` (live upload guard) and `idbBloatDetector.ts` (local-cache
 * bloat guard) used to each declare their own copy of this table. Identical
 * copies drift silently — exactly the failure mode that let the server's
 * `doc:`/`org:` budgets diverge from the client's for a long stretch before
 * this was noticed. Both modules now import from here instead.
 *
 * `org:` and `doc:` are kept in lockstep with the server's per-prefix
 * ceiling for those two prefixes — `PAYLOAD_BUDGET_BYTES['org:']` and
 * `PAYLOAD_BUDGET_BYTES['_default']` in `websocket-server/src/hocuspocus/server.js`
 * — verified by a contract test (`wsSizeBudgetConfig.contract.test.ts`) that
 * parses the server source and fails if the two sides disagree. `workflow:`
 * mirrors the server's `PAYLOAD_BUDGET_BYTES['workflow:']` the same way.
 *
 * Every other prefix here (note:, page:, space-chat:, ...) is intentionally
 * TIGHTER than the server's coarse `_default` bucket — the client pre-empts
 * long before those room types would ever approach the server's real
 * ceiling, so there's no server-side counterpart to stay in sync with.
 */

export const DEFAULT_HARD_LIMITS_BY_PREFIX: Record<string, number> = {
	'org:': 16 * 1024 * 1024,            // 16 MiB — matches server PAYLOAD_BUDGET_BYTES['org:']
	'doc:': 48 * 1024 * 1024,            // 48 MiB — matches server PAYLOAD_BUDGET_BYTES['_default']
	'workflow:': 48 * 1024 * 1024,       // 48 MiB — matches server PAYLOAD_BUDGET_BYTES['workflow:']
	'note:': 4 * 1024 * 1024,            // 4 MiB
	'page:': 4 * 1024 * 1024,            // 4 MiB
	'space:': 4 * 1024 * 1024,           // 4 MiB
	'space-chat:': 2 * 1024 * 1024,      // 2 MiB
	'conversation:': 2 * 1024 * 1024,    // 2 MiB
	'notification:': 256 * 1024,         // 256 KiB
	'window-position:': 64 * 1024,       // 64 KiB
	'terminal:': 4 * 1024 * 1024,        // 4 MiB
	'dashboard:': 4 * 1024 * 1024,       // 4 MiB
	'broadcast:': 4 * 1024 * 1024,       // 4 MiB
};

/** Catch-all for prefixes not explicitly enumerated above. */
export const FALLBACK_HARD_LIMIT_BYTES = 4 * 1024 * 1024;

/** Prefixes whose limit here must equal the server's `PAYLOAD_BUDGET_BYTES` — see the contract test. */
export const SERVER_MIRRORED_PREFIXES: Record<string, string> = {
	'org:': 'org:',
	'doc:': '_default',
	'workflow:': 'workflow:',
};
